
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface DataPoint {
  time: string;
  products: number;
}

const LiveProductChart = () => {
  const [data, setData] = useState<DataPoint[]>([]);
  const [totalProducts, setTotalProducts] = useState(1310456594);
  const [currentRate, setCurrentRate] = useState(0);
  const [currentGmtTime, setCurrentGmtTime] = useState("");
  const [isPulsing, setIsPulsing] = useState(false);
  const sourceSites = 5129543; // Number of e-commerce source sites

  useEffect(() => {
    const generateHistoricalData = () => {
      const historicalData: DataPoint[] = [];
      const now = new Date();
      
      for (let i = 19; i >= 0; i--) {
        const timePoint = new Date(now.getTime() - (i * 1000));
        const randomProducts = Math.floor(Math.random() * (170 - 110 + 1)) + 110;
        
        historicalData.push({
          time: formatTime(timePoint),
          products: randomProducts,
        });
      }
      
      return historicalData;
    };
    
    const initialData = generateHistoricalData();
    setData(initialData);
    
    if (initialData.length > 0) {
      setCurrentRate(initialData[initialData.length - 1].products);
    }
    
    updateGmtTime();

    const getRandomProductRate = () => Math.floor(Math.random() * (170 - 110 + 1)) + 110;
    
    const interval = setInterval(() => {
      const newProductCount = getRandomProductRate();
      setCurrentRate(newProductCount);
      setTotalProducts(prev => prev + newProductCount);
      updateGmtTime();
      
      setData(prevData => {
        const now = new Date();
        const newData = [...prevData.slice(1), {
          time: formatTime(now),
          products: newProductCount,
        }];
        return newData;
      });
    }, 1000);

    const pulseInterval = setInterval(() => {
      setIsPulsing(true);
      setTimeout(() => {
        setIsPulsing(false);
      }, 300);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearInterval(pulseInterval);
    };
  }, []);

  const updateGmtTime = () => {
    const now = new Date();
    const gmtDateString = now.toLocaleDateString("en-US", {
      timeZone: "GMT",
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
    
    const gmtTimeString = now.toLocaleTimeString("en-US", {
      timeZone: "GMT",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    
    setCurrentGmtTime(`${gmtDateString} ${gmtTimeString} GMT`);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRateColor = (rate: number) => {
    if (rate < 120) return "text-amber-500";
    if (rate < 150) return "text-green-500";
    return "text-blue-500";
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <CardTitle className="text-xl">Real-time Indexing</CardTitle>
            <div className="text-sm text-muted-foreground mt-1">{currentGmtTime}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-sm">
              <span className="font-normal">Total: </span>
              <span className="ml-1 font-bold">{totalProducts.toLocaleString()}</span>
            </Badge>
            <Badge variant="outline" className="text-sm">
              <span className="font-normal">Sources: </span>
              <span className="ml-1 font-bold">{sourceSites.toLocaleString()} sites</span>
            </Badge>
            <Badge variant="outline" className="text-sm flex items-center">
              <div className="relative mr-1 flex-shrink-0">
                <span 
                  className="block h-2 w-2 rounded-full bg-emerald-500"
                />
                {isPulsing && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-emerald-500"/>
                )}
              </div>
              <span className="font-normal">Current rate: </span>
              <span className={`ml-1 font-bold ${getRateColor(currentRate)}`}>
                {currentRate} products/sec
              </span>
            </Badge>
          </div>
        </div>
        <CardDescription>
          Live monitoring of products indexed per second from 5 million e-commerce sites.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 0,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="time"
                tick={{ fontSize: 12 }}
                tickMargin={5}
              />
              <YAxis 
                domain={[100, 180]}
                allowDataOverflow={true}
                tick={{ fontSize: 12 }}
                tickMargin={5}
              />
              <Tooltip
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.875rem'
                }}
                formatter={(value) => [`${value} products/sec`, 'Rate']}
                labelFormatter={(time) => `Time: ${time}`}
              />
              <Bar 
                dataKey="products" 
                fill="#3b82f6" 
                isAnimationActive={false}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveProductChart;
